<template>
  <v-card class="h-100" elevation="0" rounded="lg">
    <div v-if="widgetData.loading" class="pa-6">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" width="150" />
      <v-divider class="border-opacity-100 mb-6" color="indigo-faint" />
      <v-row>
        <v-col cols="3" class="d-flex justify-center align-center">
          <v-skeleton-loader type="avatar" size="180" class="bg-transparent" />
        </v-col>
        <v-col cols="9">
          <v-skeleton-loader
            v-for="i in 5"
            :key="i"
            type="list-item-two-line"
            class="bg-transparent mb-2"
          />
        </v-col>
      </v-row>
    </div>

    <template v-else-if="widgetData.data">
      <v-card-title class="d-flex align-center px-6 py-3">
        <div class="d-flex align-center">
          <span class="subtitle1">{{ $t("coroImpact.widgets.autoResolvedWidget.title") }}</span>
          <span class="ml-1 body1">
            ({{ $t("coroImpact.widgets.autoResolvedWidget.moduleView") }})</span
          >
        </div>
        <v-spacer></v-spacer>
        <a class="body2 coro-link text-decoration-none" @click="navigateToTickets()">
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </a>
      </v-card-title>
      <v-divider class="border-opacity-100" color="indigo-faint" />

      <v-card-text class="pb-0 px-6 pt-0">
        <div class="d-flex align-center">
          <div class="chart-wrapper pt-6 pr-6 pb-6">
            <doughnut-chart :data="chartData" :options="chartOptions" />
          </div>
          <v-divider opacity="100" color="indigo-faint" vertical inset></v-divider>
          <div class="flex-grow-1 py-6 align-self-start">
            <v-list class="pa-0" lines="one" bg-color="transparent">
              <template v-for="(item, index) in remappedModules" :key="item.id">
                <v-list-item class="px-2 py-1">
                  <div class="d-flex align-center">
                    <div class="circle mr-2" :style="{ 'background-color': item.color }"></div>
                    <v-icon :icon="`$${item.icon}`" size="40" class="mr-3"></v-icon>
                    <v-list-item-title class="body2">
                      {{ item.name }}
                    </v-list-item-title>
                  </div>
                  <template v-slot:append>
                    <div class="d-flex align-center">
                      <span class="subtitle2 mr-4">{{ item.count }}</span>
                      <v-icon icon="$check" color="success" size="24" />
                    </div>
                  </template>
                </v-list-item>

                <v-divider
                  v-if="index !== remappedModules.length - 1"
                  class="border-opacity-100 my-1"
                  color="indigo-faint"
                />
              </template>
            </v-list>
          </div>
        </div>
      </v-card-text>
    </template>

    <div v-else class="h-100 d-flex align-center justify-center flex-column pa-6">
      <v-icon icon="$alertTriangle" size="80" color="grey-lighten-1"></v-icon>
      <span class="subtitle1 mb-4 mt-4">
        {{ $t(`coroImpact.widgets.autoResolvedWidget.error`) }}
      </span>
      <div
        class="coro-link"
        data-testid="retry-fetch-data-button"
        @click="$emit('retry-fetch-data')"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useRouter } from "vue-router";
import type { DataValue, TimeFrame } from "@/types";
import DoughnutChart from "@/components/DoughnutChart.vue";
import type { AutoResolvedWidgetData } from "@/_store/coro-impact/coro-impact.module.ts";
import { useTheme } from "vuetify";
import { CoroIcons } from "@/constants/coro-icon.ts";
import { useI18n } from "vue-i18n";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { TicketsSearchSuggestion, TicketStatus } from "@/constants/tickets.ts";
import { RouteName } from "@/constants/routes.ts";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import moment from "moment/moment";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export interface ModuleViewModel {
  id: SubscriptionModule;
  name: string;
  count: number;
  icon: string;
  color: string;
}

const MODULE_CONFIG = {
  [SubscriptionModule.CLOUD_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.CLOUD_SECURITY}`,
    icon: CoroIcons.CLOUD_XS,
    colorName: "labels-purple-full",
  },
  [SubscriptionModule.ENDPOINT_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.ENDPOINT_SECURITY}`,
    icon: CoroIcons.DEVICE_XS,
    colorName: "labels-green-full",
  },
  [SubscriptionModule.EMAIL_SECURITY]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.EMAIL_SECURITY}`,
    icon: CoroIcons.EMAIL,
    colorName: "labels-red-full",
  },
  [SubscriptionModule.USER_DATA_GOVERNANCE]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.USER_DATA_GOVERNANCE}`,
    icon: CoroIcons.USER_DATA_GOVERNANCE_SM,
    colorName: "labels-blue-full",
  },
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.ENDPOINT_DATA_GOVERNANCE}`,
    icon: CoroIcons.ENDPOINT_DATA_GOVERNANCE_S,
    colorName: "labels-yellow-full",
  },
  [SubscriptionModule.EDR]: {
    i18nKey: `subscriptions.modules.${SubscriptionModule.EDR}`,
    icon: CoroIcons.EDR_S,
    colorName: "labels-orange-full",
  },
} as Record<SubscriptionModule, { i18nKey: string; icon: string; colorName: string }>;

export default defineComponent({
  components: {
    DoughnutChart,
  },
  props: {
    context: {
      type: String as PropType<"single-view" | "msp">,
      default: "single-view",
    },
    widgetData: {
      type: Object as PropType<DataValue<AutoResolvedWidgetData>>,
      required: true,
    },
    filterTimeFrame: {
      type: Object as PropType<TimeFrame>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const router = useRouter();
    const theme = useTheme();
    const i18n = useI18n();
    const filtersStore = useFiltersStore();

    const chartOptions = computed(() => {
      const delta = (props.widgetData.data ?? { delta: 0 }).delta;
      const prefix = delta > 0 ? "+" : "";
      return {
        insideTextData: {
          title: props.widgetData.data?.total,
          subtitle: i18n.t("coroImpact.widgets.autoResolvedWidget.chartSubtitle"),
          thirdLine: {
            font: "400 14px Source Sans Pro",
            text: `${prefix}${delta}% ${i18n.t("coroImpact.comparedWithPreviousPeriod")}`,
            color:
              delta > 0
                ? theme.current.value.colors["green-dark"]
                : delta < 0
                  ? theme.current.value.colors["red-dark"]
                  : theme.current.value.colors["indigo-medium"],
          },
        },
      };
    });

    const remappedModules = computed((): ModuleViewModel[] => {
      if (!props.widgetData.data?.modules) {
        return [];
      }
      return props.widgetData.data.modules
        .filter((m) => !!MODULE_CONFIG[m.name])
        .map((module) => {
          const config = MODULE_CONFIG[module.name];
          return {
            id: module.name,
            name: i18n.t(config.i18nKey),
            icon: config.icon,
            color: theme.current.value.colors[config.colorName],
            count: module.count,
          };
        });
    });

    const chartData = computed(() => {
      if (!remappedModules.value.length) {
        return { labels: [], datasets: [] };
      }
      return {
        labels: remappedModules.value.map((item) => `${item.name} — ${item.count}`),
        datasets: [
          {
            backgroundColor: remappedModules.value.map((item) => item.color),
            hoverBackgroundColor: remappedModules.value.map((item) => item.color),
            data: remappedModules.value.map((item) => item.count),
            borderWidth: 0,
            cutout: "80%",
          },
        ],
      };
    });

    const navigateToTickets = () => {
      const route = props.context === "msp" ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS;
      const context = props.context === "msp" ? FilterContext.MSP_TICKETS : FilterContext.TICKETS;

      trackGtmEvent(
        GtmAction.CORO_INSIGHTS_PAGE,
        `${startCase(props.context)} - ${GTMParams.AUTO_RESOLVED_TICKETS_ALL}`
      );

      const startTime = moment(props.filterTimeFrame.start).startOf("day").utc().valueOf();
      filtersStore.setFilters(context, {
        eventTimeRange: {
          start: "",
          end: "",
        },
        status: TicketStatus.CLOSED,
        search: `${TicketsSearchSuggestion.AUTO_RESOLVED_FROM_TIME}${startTime}`,
      });
      return router.push({ name: route });
    };

    return {
      remappedModules,
      navigateToTickets,
      chartData,
      chartOptions,
    };
  },
});
</script>

<style scoped lang="scss">
.view-all-btn {
  text-decoration: none !important;
  user-select: none;

  &:hover span {
    text-decoration: underline !important;
  }
}
</style>
